import { useCallback } from "react";
import { IconType } from "../../icons/Icon";
import { IDropdownMenuItem } from "../../components/AppDropdownMenu";
import { usePermissions } from "../../infrastructure/hooks/usePermissions";
import { showCustomConfirmDialog } from "../../components/dialogs/AppConfirmDialog";
import { ICategorizedNomenclatureEndpoints } from "../models/categorizedNomenclatureEndpoints";

interface IUseCategoryMenuItemsParams {
  endpoints: ICategorizedNomenclatureEndpoints;
  onAddItem: (categoryId: number | null) => void;
  onEditCategory: (categoryId: number) => void;
  onReorderItems: (categoryId: number | null) => void;
  onDeleteCategory: (categoryId: number) => void;
}

export const useCategoryMenuItems = ({
  endpoints,
  onAddItem,
  onEditCategory,
  onReorderItems,
  onDeleteCategory,
}: IUseCategoryMenuItemsParams) => {
  const { userHavePermissions } = usePermissions();

  const getCategoryMenuItems = useCallback(
    (categoryId: number, itemCount: number): IDropdownMenuItem[] => {
      const menuItems: IDropdownMenuItem[] = [];

      if (userHavePermissions(endpoints.create)) {
        menuItems.push({
          label: "Добави елемент",
          icon: IconType.PlusIcon,
          onClick: () => onAddItem(categoryId),
        });
      }

      if (itemCount > 1 && userHavePermissions(endpoints.reorderItems)) {
        menuItems.push({
          label: "Подреди елементи",
          icon: IconType.DragHandle,
          onClick: () => onReorderItems(categoryId),
        });
      }

      if (userHavePermissions(endpoints.updateCategory)) {
        menuItems.push({
          label: "Редактирай категория",
          icon: IconType.EditIcon,
          onClick: () => onEditCategory(categoryId),
        });
      }

      if (userHavePermissions(endpoints.toggleCategoryDeletionStatus)) {
        menuItems.push({
          label: "Изтрий категория",
          icon: IconType.DeleteIcon,
          disabled: itemCount > 0,
          tooltip:
            itemCount > 0
              ? "Преместете или изтрийте елементите в тази категория"
              : undefined,
          onClick: () => {
            showCustomConfirmDialog(
              "Сигурни ли сте, че искате да изтриете тази категория?",
              () => onDeleteCategory(categoryId)
            );
          },
        });
      }

      return menuItems;
    },
    [
      endpoints,
      onAddItem,
      onEditCategory,
      onReorderItems,
      onDeleteCategory,
      userHavePermissions,
    ]
  );

  const getUncategorizedMenuItems = useCallback(
    (itemCount: number): IDropdownMenuItem[] => {
      const menuItems: IDropdownMenuItem[] = [];

      if (userHavePermissions(endpoints.create)) {
        menuItems.push({
          label: "Добави елемент",
          icon: IconType.PlusIcon,
          onClick: () => onAddItem(null),
        });
      }

      if (itemCount > 1 && userHavePermissions(endpoints.reorderItems)) {
        menuItems.push({
          label: "Подреди елементи",
          icon: IconType.DragHandle,
          onClick: () => onReorderItems(null),
        });
      }

      return menuItems;
    },
    [endpoints, onAddItem, onReorderItems, userHavePermissions]
  );

  return {
    getCategoryMenuItems,
    getUncategorizedMenuItems,
  };
};
